/**
 * Presentation helpers. Every amount reaching this module is integer cents.
 */

import {
  COMPENSATION_PERIOD_LABELS,
  EMPLOYMENT_TYPE_LABELS,
  SENIORITY_LABELS,
  WORKPLACE_TYPE_LABELS,
  formatCents,
  type Compensation,
  type EmploymentType,
  type Job,
  type JobLocation,
  type Seniority,
  type WorkplaceType,
} from '@bidtohire/shared';

export { formatCents };

/** A money figure with cents shown only when the amount is not whole dollars. */
export function formatMoneyCents(cents: number, currency = 'USD'): string {
  const whole = cents % 100 === 0;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: whole ? 0 : 2,
    maximumFractionDigits: whole ? 0 : 2,
  }).format(cents / 100);
}

export function seniorityLabel(value: Seniority): string {
  return SENIORITY_LABELS[value];
}

export function workplaceLabel(value: WorkplaceType): string {
  return WORKPLACE_TYPE_LABELS[value];
}

export function employmentLabel(values: readonly EmploymentType[]): string {
  return values.map((value) => EMPLOYMENT_TYPE_LABELS[value]).join(', ');
}

/** "$120,000 – $160,000 per year", the required salary range. */
export function compensationLabel(compensation: Compensation | undefined): string | undefined {
  if (compensation === undefined) return undefined;
  const { minCents, maxCents, currency, period } = compensation;
  if (minCents === undefined && maxCents === undefined) return undefined;

  const range =
    minCents !== undefined && maxCents !== undefined && minCents !== maxCents
      ? `${formatMoneyCents(minCents, currency)} – ${formatMoneyCents(maxCents, currency)}`
      : formatMoneyCents((minCents ?? maxCents) as number, currency);

  const suffix = compensation.isEstimated ? ' (estimated)' : '';
  return `${range} ${COMPENSATION_PERIOD_LABELS[period]}${suffix}`;
}

export function locationLabel(
  locations: readonly JobLocation[],
  workplaceType: WorkplaceType,
): string {
  if (locations.length === 0) {
    return workplaceType === 'REMOTE' ? 'Remote' : 'Location not stated';
  }
  const first = locations[0] as JobLocation;
  const parts = [first.city, first.region, first.countryIso2].filter(
    (part): part is string => part !== undefined && part !== '',
  );
  const primary = parts.join(', ');
  const extra = locations.length > 1 ? ` +${locations.length - 1} more` : '';
  const prefix = workplaceType === 'REMOTE' ? 'Remote — ' : '';
  return `${prefix}${primary}${extra}`;
}

/**
 * The board's salary cell has 168px to work in, so the figures are abbreviated
 * there: "$255,000 – $310,000 per year" becomes "$255k – $310k". A period other
 * than a year is kept, because an hourly rate read as a salary would mislead.
 */
export function compactCompensationLabel(label: string | undefined): string | undefined {
  if (label === undefined || label === '') return undefined;
  const abbreviated = label.replace(/\$(\d{1,3}(?:,\d{3})+)/g, (whole, digits: string) => {
    const value = Number(digits.replace(/,/g, ''));
    if (value >= 1_000_000) {
      const millions = value / 1_000_000;
      return `$${millions % 1 === 0 ? millions : millions.toFixed(1)}m`;
    }
    if (value >= 10_000) return `$${Math.round(value / 1000)}k`;
    return whole;
  });

  return abbreviated
    .replace(/ per year\b/, '')
    .replace(/ per hour\b/, '/hr')
    .replace(/ per day\b/, '/day')
    .replace(/ per week\b/, '/wk')
    .replace(/ per month\b/, '/mo');
}

/**
 * `numeric: 'always'`, so an age is never rendered as a calendar word. "auto"
 * prints "yesterday" for anything between one and one-and-a-half days old,
 * which names the wrong calendar day whenever that span crosses two midnights
 * — and it leaves the compact board saying "1d ago" about the very same row
 * the feed beside it calls "yesterday".
 */
const RELATIVE = new Intl.RelativeTimeFormat('en-US', { numeric: 'always' });

type Unit = 'second' | 'minute' | 'hour' | 'day' | 'month';

const SUFFIX: Readonly<Record<Unit, string>> = {
  second: 's',
  minute: 'm',
  hour: 'h',
  day: 'd',
  month: 'mo',
};

/**
 * How long ago, as one bucket both formatters share.
 *
 * The magnitude is floored, never rounded. Rounding inside a bucket whose
 * ceiling is the next unit prints that ceiling as a unit of its own: 23.6
 * hours becomes "24h ago", 59.6 minutes becomes "60m ago".
 */
function elapsed(iso: string, now: number): { value: number; unit: Unit } | undefined {
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return undefined;
  const seconds = Math.max(0, Math.floor((now - then) / 1000));
  if (seconds < 60) return { value: seconds, unit: 'second' };
  if (seconds < 3600) return { value: Math.floor(seconds / 60), unit: 'minute' };
  if (seconds < 86_400) return { value: Math.floor(seconds / 3600), unit: 'hour' };
  if (seconds < 2_592_000) return { value: Math.floor(seconds / 86_400), unit: 'day' };
  return { value: Math.floor(seconds / 2_592_000), unit: 'month' };
}

/** "12s", "4m", "3h", "2d" — the board and the ticker have no room for more. */
export function shortTimeAgo(iso: string, now: number = Date.now()): string {
  const age = elapsed(iso, now);
  if (age === undefined) return '';
  return `${age.value}${SUFFIX[age.unit]}`;
}

/**
 * "2 minutes ago", "3 days ago" — used by the activity feed and the listing
 * meta line. It reads the same bucket `shortTimeAgo` does, so one row can never
 * be "1d ago" in one panel and "23 hours ago" in another.
 */
export function timeAgo(iso: string, now: number = Date.now()): string {
  const age = elapsed(iso, now);
  if (age === undefined) return '';
  return RELATIVE.format(-age.value, age.unit);
}

const DATE = new Intl.DateTimeFormat('en-US', {
  year: 'numeric',
  month: 'long',
  day: 'numeric',
});

export function formatDate(iso: string): string {
  const parsed = Date.parse(iso);
  return Number.isNaN(parsed) ? '' : DATE.format(parsed);
}

const DATE_TIME = new Intl.DateTimeFormat('en-US', {
  month: 'long',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
});

/**
 * "August 24 at 12:00 AM" — used where an allowance resets. The instant is UTC
 * midnight; it is shown in the reader's own zone, because that is the clock
 * they will be watching.
 */
export function formatDateTime(iso: string): string {
  const parsed = Date.parse(iso);
  return Number.isNaN(parsed) ? '' : DATE_TIME.format(parsed).replace(', ', ' at ');
}

const SHORT_DATE = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short' });

/** "9 Aug" — for axis labels and other places a full date would wrap. */
export function formatShortDate(iso: string): string {
  const parsed = Date.parse(iso);
  return Number.isNaN(parsed) ? '' : SHORT_DATE.format(parsed);
}

/** The salary figures without their period, which is stated separately. */
export function salaryFiguresOnly(label: string | undefined): string | undefined {
  if (label === undefined || label === '') return undefined;
  return label.replace(/ per (year|hour|day|week|month)\b/, '');
}

/** "per year" → "Per year", for the caption beneath the figures. */
export function salaryPeriodOnly(label: string | undefined): string | undefined {
  const match = label?.match(/ per (year|hour|day|week|month)\b/);
  return match === null || match === undefined ? undefined : `Per ${match[1]}`;
}

/** Whole days remaining before a listing lapses; never negative. */
export function daysRemaining(validThrough: string, now: number = Date.now()): number {
  const ends = Date.parse(validThrough);
  if (Number.isNaN(ends)) return 0;
  return Math.max(0, Math.ceil((ends - now) / 86_400_000));
}

export function isExpired(job: Pick<Job, 'status' | 'validThrough'>, now = Date.now()): boolean {
  if (job.status === 'EXPIRED') return true;
  const ends = Date.parse(job.validThrough);
  return !Number.isNaN(ends) && ends <= now;
}

/** Full URL of an uploaded asset, given the key stored on the record. */
export function assetUrl(basePath: string, key: string | undefined): string | undefined {
  if (key === undefined || key === '') return undefined;
  return `${basePath}/${key}`;
}

/* -------------------------------------------------------------------------- */
/* Company marks                                                              */
/* -------------------------------------------------------------------------- */

/**
 * The palette, the hash, and the initials themselves live in the shared
 * workspace. Three renderers draw the same mark — the board, the hero's rocket
 * layer painting onto a canvas texture, and the share-card Lambda painting a
 * listing into the PNG a link preview shows — and a company whose initials are
 * indigo on the board must be indigo in all three. They are re-exported here
 * so every caller still names one module for presentation helpers.
 */
export { MARK_COLOURS, initials, markColour } from '@bidtohire/shared';
